import { useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { FiEdit2, FiTrash2, FiUsers } from "react-icons/fi";
import { ModuleLayout } from "../../../layouts";
import { ContentCard, Button, Pagination, Loader } from "../../../components";
import { ROUTES_CONSTANTS, QUERY_CONSTANTS } from "../../../constants";
import {
  StatCard,
  StatusBadge,
  SectionHeader,
  ActionIconButton,
  ConfirmModal,
} from "../components";
import { getPermissionGroupsApi, deletePermissionGroupApi } from "../../../DAL";

const PREVIEW_COUNT = 4;

const GroupCard = ({ group, onEdit, onDelete }) => {
  const { t } = useTranslation();
  const previewPerms = group.permissions?.slice(0, PREVIEW_COUNT) ?? [];
  const extra = (group.permissions?.length ?? 0) - PREVIEW_COUNT;

  return (
    <div className="bg-white border border-[#E3E3E3] rounded-[10px] p-5 flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-semibold text-black">
            {group.display_name || group.name}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">{group.description}</p>
        </div>
        <StatusBadge status={group.is_active ? "Active" : "Inactive"} filled />
      </div>

      {/* Permissions preview */}
      <div>
        <p className="text-xs font-medium text-gray-500 mb-2">{t('permissionGroups.cardPermissions')}</p>
        <div className="flex flex-wrap gap-1.5">
          {previewPerms.map((p) => (
            <span
              key={p.id}
              className="px-2 py-0.5 border border-[#E3E3E3] rounded text-xs text-gray-600 bg-gray-50"
            >
              {p.display_name}
            </span>
          ))}
          {extra > 0 && (
            <span className="px-2 py-0.5 border border-[#E3E3E3] rounded text-xs text-gray-400 bg-gray-50">
              +{extra} more
            </span>
          )}
        </div>
      </div>

      {/* Users count */}
      <div className="flex items-center gap-1.5 text-xs text-gray-400">
        <FiUsers className="w-3.5 h-3.5" />
        <span>{group.users_count ?? 0} {t('permissionGroups.cardUsersCount')}</span>
      </div>

      {/* Actions */}
      <div className="flex items-center pt-1 border-t border-[#f0f0f0] gap-2">
        <ActionIconButton icon={FiEdit2} onClick={onEdit} variant="outlined" />
        <ActionIconButton
          icon={FiTrash2}
          onClick={onDelete}
          danger
          variant="outlined"
        />
      </div>
    </div>
  );
};

const PermissionGroupsList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // ── Data ────────────────────────────────────────────────────────────────────
  const { data: groupsData, isLoading } = useQuery({
    queryKey: [QUERY_CONSTANTS.PERMISSION_GROUPS, page],
    queryFn: () => getPermissionGroupsApi(page),
  });
  const groups = groupsData?.data ?? [];
  const apiStats = groupsData?.stats ?? {};
  const pagination = groupsData?.pagination ?? {};

  const stats = [
    { value: apiStats.total ?? pagination.total ?? groups.length, label: t('permissionGroups.statTotal') },
    { value: apiStats.active ?? groups.filter((g) => g.is_active).length, label: t('common.active') },
  ];

  // ── Delete ───────────────────────────────────────────────────────────────────
  const { mutate: deleteGroup, isPending: isDeleting } = useMutation({
    mutationFn: () => deletePermissionGroupApi(deleteTarget.id),
    onSuccess: (response) => {
      enqueueSnackbar(response?.message, { variant: "success" });
      queryClient.invalidateQueries({
        queryKey: [QUERY_CONSTANTS.PERMISSION_GROUPS],
      });
      setDeleteTarget(null);
    },
  });

  return (
    <ModuleLayout
      title={t('permissionGroups.title')}
      onBack={() => navigate(ROUTES_CONSTANTS.COMPANY_DASHBOARD)}
    >
      <p className="text-sm text-gray-500 mb-4">
        {t('permissionGroups.breadcrumb')}
      </p>

      <ContentCard className="h-full">
        <SectionHeader
          title={t('permissionGroups.sectionTitle')}
          subtitle={t('permissionGroups.sectionSubtitle')}
          action={
            <Button
              onClick={() => navigate(ROUTES_CONSTANTS.CREATE_PERMISSION_GROUP)}
            >
              {t('permissionGroups.btnCreate')}
            </Button>
          }
        />

        {/* Stats */}
        <div className="flex gap-4 mb-6">
          {stats.map((s) => (
            <StatCard key={s.label} value={s.value} label={s.label} />
          ))}
        </div>

        {/* Group Cards Grid */}
        {isLoading ? (
          <Loader />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {groups.map((group) => (
              <GroupCard
                key={group.id}
                group={group}
                onEdit={() =>
                  navigate(ROUTES_CONSTANTS.UPDATE_PERMISSION_GROUP, {
                    state: { id: group.id },
                  })
                }
                onDelete={() => setDeleteTarget(group)}
              />
            ))}
          </div>
        )}

        <Pagination
          currentPage={pagination.current_page ?? page}
          lastPage={pagination.last_page}
          onPageChange={setPage}
        />
      </ContentCard>

      {deleteTarget && (
        <ConfirmModal
          title={t('permissionGroups.deleteTitle')}
          confirmLabel={t('common.delete')}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={deleteGroup}
          loading={isDeleting}
        >
          <p>
            {t('permissionGroups.deleteConfirmPre')}{" "}
            <span className="text-primary-500 font-medium">
              [{deleteTarget.display_name || deleteTarget.name}]
            </span>
            ? {t('common.undone')}
          </p>
        </ConfirmModal>
      )}
    </ModuleLayout>
  );
};

export default PermissionGroupsList;
